import React, { useState } from "react";
import moment from "moment";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import Teachr from "../../Objects/models/Teachr";
import DialogLayout from "../../Components/DialogLayout";
import PunctualCourseRepository from "../../Repositories/PunctualCourseRepository";
import { SHOW_ERROR, SHOW_SUCCESS } from "../../Redux/Reducers/MessageReducers";
import { DV2 } from "../DeclarationV2/theme";
import { euros, monthName, plural } from "../DeclarationV2/format";
import { vocab } from "../DeclarationV2/vocabulary";
import { Avatar, Badge, Button, Card, Notice, TextLink, useDv2Theme } from "../DeclarationV2/layout/Ui";
import { Draft, DraftGroup } from "./types";
import { draftDatesLabel, draftReceived, draftScheduleLabel } from "./drafts";
import useDrafts, { DRAFTS_PAGE_SIZE } from "./useDrafts";
import useDeclareDraft, { DraftRefusal } from "./useDeclareDraft";
import { draftEditPath, legacyDraftsPath } from "./paths";
import QuickDeclarationExplainer from "./QuickDeclarationExplainer";

const initialsOf = (first: string, last: string) => `${first.charAt(0)}${last.charAt(0)}`.toUpperCase() || "?";
const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
const GRID = "1.5fr 1.2fr 1fr 1fr 0.9fr auto";

/** « Allemand, 2 brouillons » */
const groupSubtitle = (group: DraftGroup): string => {
    const subjects = Array.from(new Set(group.drafts.map((d) => d.subjectLabel).filter(Boolean))).map(capitalize);
    const count = plural(group.drafts.length, "brouillon");
    return subjects.length > 0 ? `${subjects.join(", ")}, ${count}` : count;
};

interface RowProps {
    draft: Draft;
    teachr: Teachr;
    busy: boolean;
    onEdit: () => void;
    onDeclare: () => void;
    onDelete: () => void;
}

/** Ligne web (tableau) et carte mobile d'un brouillon (maquettes 1g, 2g). */
const DraftRow = ({ draft, teachr, busy, onEdit, onDeclare, onDelete }: RowProps) => {
    const { fontFamily, text } = useDv2Theme();
    // Ancien cours sans détail des séances : les jours sont à choisir avant de pouvoir déclarer.
    const daysToPick = draft.needsDates && draft.sessions.length === 0;
    const received = draftReceived(draft, teachr);
    const amount = daysToPick ? "À calculer" : euros(received);
    const schedule = daysToPick ? "Déclaration globale le mois dernier" : draftScheduleLabel(draft);
    const format = draft.multiple ? vocab.multipleCoursesTitle() : vocab.singleCourseTitle();
    const actions = (
        <div className="flex gap-2 items-center justify-end flex-wrap">
            {daysToPick ? (
                <Button label="Choisir les jours" size="sm" onClick={onEdit} disabled={busy} />
            ) : (
                <>
                    <Button label="Modifier" variant="secondary" size="sm" onClick={onEdit} disabled={busy} />
                    <Button label="Déclarer" size="sm" onClick={onDeclare} disabled={busy} />
                </>
            )}
            <button
                type="button"
                onClick={onDelete}
                disabled={busy}
                aria-label="Supprimer ce brouillon"
                title="Supprimer ce brouillon"
                style={{ width: 34, height: 34, borderRadius: 10, background: DV2.pageBg, border: "none", color: DV2.placeholder, cursor: "pointer", display: "grid", placeItems: "center", fontSize: 15, fontFamily }}
            >
                🗑
            </button>
        </div>
    );
    return (
        <>
            {/* web */}
            <div className="hidden md:grid items-center gap-3" style={{ gridTemplateColumns: GRID, padding: "14px 20px 14px 76px", borderTop: `1px solid ${DV2.borderSoft}`, fontFamily, color: text }}>
                <div style={{ fontWeight: 800, fontSize: 14 }}>{draftDatesLabel(draft)}</div>
                <div style={{ fontSize: 14 }}>{schedule}</div>
                <div style={{ fontSize: 13 }}>{format}</div>
                <div style={{ fontWeight: 800, fontSize: 14 }}>{amount}</div>
                <div><Badge tone="warning">Brouillon</Badge></div>
                {actions}
            </div>
            {/* mobile */}
            <div className="md:hidden flex flex-col gap-2" style={{ padding: "14px 16px", borderTop: `1px solid ${DV2.borderSoft}`, fontFamily, color: text }}>
                <div className="flex justify-between gap-3">
                    <div style={{ fontWeight: 800, fontSize: 14 }}>{draftDatesLabel(draft)}</div>
                    <div style={{ fontWeight: 800, fontSize: 14 }}>{amount}</div>
                </div>
                <div style={{ fontSize: 13 }}>{schedule}, {format.toLowerCase()}</div>
                <div className="flex justify-between items-center gap-3">
                    <Badge tone="warning">Brouillon</Badge>
                    {actions}
                </div>
            </div>
        </>
    );
};

/**
 * Brouillons v2 (maquettes 1g et 2g) : les cours du mois précédent repris
 * pour le mois en cours, un accordéon par élève (replié, un clic déplie ses
 * brouillons), Modifier, Déclarer et Supprimer visibles sans menu, dix élèves
 * par page. Même source et même logique que l'onglet « Brouillons » existant,
 * qui reste en place.
 */
const DraftsList = ({ teachr, embedded = false }: { teachr: Teachr; /** Intégrée dans un onglet de « Mes cours » : sans en-tête ni marges de page. */ embedded?: boolean }) => {
    const { fontFamily, text, primary } = useDv2Theme();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { groups, total, pageCount, page, loading, failed, reload, loadPage } = useDrafts(teachr);
    const [toDelete, setToDelete] = useState<Draft | null>(null);
    const [deleting, setDeleting] = useState(false);
    const [open, setOpen] = useState<Set<string>>(new Set());

    const toggle = (key: string) => setOpen((prev) => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key); else next.add(key);
        return next;
    });

    const { declare, dialogs, busy } = useDeclareDraft(teachr, {
        onRefused: (draft: Draft, refusal: DraftRefusal) => navigate(draftEditPath(draft.id), { state: { fromDeclare: true, overlap: refusal.overlap ?? null } }),
        onDeclared: () => reload(),
    });

    const remove = async () => {
        if (!toDelete) return;
        setDeleting(true);
        try {
            await PunctualCourseRepository.deletePunctualByTeachrDashboard(toDelete.id);
            dispatch({ type: SHOW_SUCCESS, payload: "Le brouillon a bien été supprimé." });
            setToDelete(null);
            reload();
        } catch {
            dispatch({ type: SHOW_ERROR, payload: "La suppression du brouillon a échoué. Réessayez dans quelques instants.", title: "Attention !" });
        } finally {
            setDeleting(false);
        }
    };

    // Retour : la page d'où l'on vient (Mes cours ou l'accueil), sinon Mes cours.
    const back = () => (window.history.length > 1 ? navigate(-1) : navigate(legacyDraftsPath()));

    const month = capitalize(monthName(moment()));
    const previous = monthName(moment().subtract(1, "month"));
    const from = total === 0 ? 0 : page * DRAFTS_PAGE_SIZE + 1;
    const to = Math.min(total, (page + 1) * DRAFTS_PAGE_SIZE);
    const draftsOnPage = groups.reduce((n, g) => n + g.drafts.length, 0);

    return (
        <div style={{ fontFamily, color: text }} className="px-4 w-full">
            {dialogs}
            <DialogLayout
                title="Suppression du brouillon"
                text={toDelete ? (
                    <p style={{ fontFamily, fontSize: 15, lineHeight: 1.55, color: text, margin: 0 }}>
                        Vous êtes sur le point de supprimer le brouillon {vocab.ofTheCourse()} de <strong>{toDelete.student.firstName} {toDelete.student.lastName}</strong>, {draftDatesLabel(toDelete).toLowerCase()}.
                        Si vous souhaitez confirmer, cliquez sur <strong>Supprimer</strong>.
                    </p>
                ) : null}
                valid="Supprimer"
                cancel="Annuler"
                open={!!toDelete}
                load={deleting}
                handleSubmit={remove}
                handleClose={() => { if (!deleting) setToDelete(null); }}
            />
            <div className={embedded ? "w-full flex flex-col gap-5" : "mx-auto w-full /max-w-6xl px-4 sm:px-6 py-4 lg:py-8 flex flex-col gap-5"}>
                {!embedded && (
                <header className="flex items-center gap-3">
                    <button
                        type="button"
                        onClick={back}
                        aria-label="Retour"
                        title="Retour"
                        style={{ width: 40, height: 40, borderRadius: 12, background: "#fff", border: `1px solid ${DV2.border}`, color: primary, fontSize: 22, display: "grid", placeItems: "center", cursor: "pointer", flexShrink: 0 }}
                    >
                        ‹
                    </button>
                    <div className="min-w-0">
                        <h1 style={{ fontSize: 24, fontWeight: 800, margin: 0, lineHeight: 1.2 }} className="lg:text-[26px]">Déclaration rapide des {vocab.courses()} du mois</h1>
                        <p style={{ fontSize: 14, margin: "2px 0 0", lineHeight: 1.55 }}>
                            {month} {moment().format("YYYY")} : vos {vocab.courses()} de {previous} repris en brouillon pour ce mois. Vérifiez chaque ligne, modifiez si besoin, puis déclarez. Rien n'est transmis tant que vous ne déclarez pas.
                        </p>
                    </div>
                </header>
                )}

                <QuickDeclarationExplainer />

                {failed && (
                    <Notice tone="danger" action={<TextLink label="Réessayer" onClick={reload} />}>Les brouillons n'ont pas pu être chargés.</Notice>
                )}
                {loading && groups.length === 0 && (
                    <div style={{ padding: 32, textAlign: "center", fontSize: 14 }}>
                        <CircularProgress size={18} style={{ marginRight: 8, verticalAlign: "middle" }} /> Chargement de vos brouillons…
                    </div>
                )}
                {!loading && !failed && groups.length === 0 && (
                    <Card style={{ textAlign: "center", padding: 32 }}>
                        <div style={{ fontSize: 28 }} aria-hidden>📝</div>
                        <div style={{ fontWeight: 800, fontSize: 16, marginTop: 8 }}>Aucun brouillon en attente</div>
                        <div style={{ fontSize: 14, marginTop: 4 }}>Les {vocab.courses()} que vous déclarez ce mois seront proposés en brouillon le mois prochain.</div>
                    </Card>
                )}

                {groups.map((group) => {
                    const isOpen = open.has(group.key);
                    const panelId = `bv2-group-${group.key}`;
                    return (
                        <Card key={group.key} padding={0} style={{ overflow: "hidden", opacity: loading ? 0.6 : 1 }} as="section" aria-label={`Brouillons de ${group.student.firstName} ${group.student.lastName}`}>
                            <button
                                type="button"
                                onClick={() => toggle(group.key)}
                                aria-expanded={isOpen}
                                aria-controls={panelId}
                                className="w-full flex items-center gap-3 text-left"
                                style={{ padding: "16px 20px", background: isOpen ? DV2.tint : "transparent", border: "none", cursor: "pointer", fontFamily, color: text }}
                            >
                                <Avatar text={initialsOf(group.student.firstName, group.student.lastName)} size={44} />
                                <div style={{ minWidth: 0, flex: 1 }}>
                                    <div style={{ fontWeight: 800, fontSize: 16 }}>{group.student.firstName} {group.student.lastName}</div>
                                    <div style={{ fontSize: 13 }}>{groupSubtitle(group)}</div>
                                </div>
                                <span aria-hidden style={{ color: primary, fontSize: 20, lineHeight: 1, transform: isOpen ? "rotate(90deg)" : "none", transition: "transform .15s", flexShrink: 0 }}>›</span>
                            </button>
                            {isOpen && (
                                <div id={panelId}>
                                    <div className="hidden md:grid" style={{ gridTemplateColumns: GRID, padding: "8px 20px 8px 76px", gap: 12, fontSize: 12, fontWeight: 800, color: DV2.placeholder, textTransform: "uppercase", letterSpacing: ".05em", borderTop: `1px solid ${DV2.borderSoft}` }}>
                                        <span>Date</span><span>Horaire</span><span>Format</span><span>Vous recevez</span><span>Statut</span><span />
                                    </div>
                                    {group.drafts.map((draft) => (
                                        <DraftRow
                                            key={draft.id}
                                            draft={draft}
                                            teachr={teachr}
                                            busy={busy}
                                            onEdit={() => navigate(draftEditPath(draft.id))}
                                            onDeclare={() => declare(draft)}
                                            onDelete={() => setToDelete(draft)}
                                        />
                                    ))}
                                </div>
                            )}
                        </Card>
                    );
                })}

                {total > 0 && (
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" style={{ fontSize: 13 }}>
                        <span>
                            {vocab.clients().charAt(0).toUpperCase() + vocab.clients().slice(1)} {from} à {to} sur {total}, {plural(draftsOnPage, "brouillon")} sur cette page.
                            {" "}Un brouillon déclaré compte pour {vocab.aCourse()} sur les 8 du mois de l'élève.
                        </span>
                        {pageCount > 1 && (
                            <nav aria-label="Pagination" style={{ display: "flex", gap: 6, alignItems: "center" }}>
                                <Button label="‹" variant="secondary" size="sm" ariaLabel="Page précédente" onClick={() => loadPage(page - 1)} disabled={page === 0 || loading} />
                                {Array.from({ length: pageCount }, (_, i) => (
                                    <button
                                        key={i}
                                        type="button"
                                        onClick={() => loadPage(i)}
                                        aria-current={i === page ? "page" : undefined}
                                        disabled={loading}
                                        style={{ padding: "6px 10px", borderRadius: 8, fontFamily, fontWeight: 800, cursor: "pointer", background: i === page ? primary : "#fff", color: i === page ? "#fff" : text, border: i === page ? "none" : `1px solid ${DV2.border}` }}
                                    >
                                        {i + 1}
                                    </button>
                                ))}
                                <Button label="›" variant="secondary" size="sm" ariaLabel="Page suivante" onClick={() => loadPage(page + 1)} disabled={page >= pageCount - 1 || loading} />
                            </nav>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default DraftsList;
